import { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  IconLoader,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import { clearSettingStateState } from './store/slices/form';
import SettingForm from '../../components/settings/form';
import {
  useCreateRiskScoreRangeMutation,
  useLazyGetRiskScoreRangesQuery,
  useLazyGetRiskScoreRangeQuery,
  useUpdateRiskScoreRangeMutation,
} from './store/services';
import { RiskScoreRange, RiskScoreRangeFormValues } from '../types';

import ROUTES from '../../routes';

import cn from './risk-levels.module.css';

export const schema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 3,
      errorMessage: {
        minLength: 'Category Name is required. Min Length 3 char',
      },
    },
    rangeStart: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk score from is required.',
      },
    },
    rangeEnd: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Risk score to is required.',
      },
    },
    action: {
      type: 'string',
      minLength: 1,
      errorMessage: {
        minLength: 'Scenario is required',
      },
    },
  },
  required: ['name', 'rangeStart', 'rangeEnd', 'action'],
};

const SettingFormHOC = () => {
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [defaultFormValuesValues, setDefaultFormValues] = useState({
    action: '',
    name: '',
    rangeEnd: '',
    rangeStart: '',
  });
  const [riskLevelRangeError, setRiskLevelRangeError] = useState('');
  const [createError, setCreateError] = useState('');

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setError,
    watch,
    clearErrors,
  } = useForm<RiskScoreRangeFormValues>({
    resolver: ajvResolver(schema as any),
    defaultValues: {
      action: '',
      name: '',
      rangeEnd: '',
      rangeStart: '',
    },
  });

  const fields = watch();

  const { formValues } = useAppSelector((state) => state.riskLevels.form);

  const isDirty = !isEqual(defaultFormValuesValues, fields);

  const { activePermissions } = useAppSelector(
    (state) => state.amlDictionaries,
  );

  const [create, { isLoading: requestInProgress, isSuccess: submitSuccess }] =
    useCreateRiskScoreRangeMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateIsSuccess }] =
    useUpdateRiskScoreRangeMutation();
  const [getList] = useLazyGetRiskScoreRangesQuery();
  const [getRiskScoreRange, { isLoading, isFetching }] =
    useLazyGetRiskScoreRangeQuery();

  const { settingId } = useParams();
  const { search } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onFormSubmit = handleSubmit(async (values) => {
    setCreateError('');
    setRiskLevelRangeError('');

    const body = {
      name: values.name,
      action: values.action,
      rangeStart: values.rangeStart,
      rangeEnd: values.rangeEnd,
    };

    if (Number(values.rangeStart) > Number(values.rangeEnd)) {
      setRiskLevelRangeError(
        'Risk score from can`t be bigger then Risk score to',
      );
    } else {
      let response;

      let requestAvailable = true;

      if (
        Number.isNaN(Number(values.rangeStart)) ||
        Number.isNaN(Number(values.rangeEnd))
      ) {
        requestAvailable = false;

        if (Number.isNaN(Number(values.rangeStart))) {
          setError('rangeStart', {
            type: 'custom',
            message: 'Risk Score from must be an integer number',
          });
        }

        if (Number.isNaN(Number(values.rangeEnd))) {
          setError('rangeEnd', {
            type: 'custom',
            message: 'Risk Score to must be an integer number',
          });
        }
      }

      if (requestAvailable) {
        if (settingId) {
          response = (await update({ id: settingId, body })) as {
            data?: RiskScoreRange;
            error: ResponseError;
          };
        } else {
          response = (await create(body)) as {
            data?: RiskScoreRange;
            error: ResponseError;
          };
        }

        if (response?.error) {
          responseErrorsResolver({
            errors: response.error,
            setError,
            setGlobalError: setCreateError,
          });
        } else if (!response?.error && !settingId) {
          getList('?take=30');
        }
      }
    }
  });

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setCreateError('');
    clearErrors(['rangeStart', 'rangeEnd']);

    setTimeout(() => {
      navigate(`${ROUTES.RISK_LEVELS}${search || ''}`);
    }, 100);
  }, []);

  useEffect(() => {
    if (submitSuccess) {
      handleClose();
    }
  }, [submitSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (settingId) {
      getRiskScoreRange(settingId);
    }

    return () => {
      dispatch(clearSettingStateState());
    };
  }, [dispatch, settingId]);

  useEffect(() => {
    if (updateIsSuccess && formValues) {
      setDefaultFormValues(formValues);
    }
  }, [updateIsSuccess, formValues]);

  useEffect(() => {
    if (formValues && settingId) {
      reset(formValues);

      setDefaultFormValues(formValues);
    }
  }, [reset, formValues, settingId]);

  const isEditable = activePermissions?.includes(
    PermissionEnum.SETTINGS_RISK_SCORE_RANGES_EDIT_ACTION,
  );

  let title;

  if (settingId) {
    title = 'Edit category';

    if (!isEditable) {
      title = 'Category info';
    }
  } else {
    title = 'Create category';
  }

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.settingFormLoader}>
        <IconLoader />
      </div>
    );
  } else if (
    !isLoading &&
    !isFetching &&
    activePermissions?.includes(
      PermissionEnum.SETTINGS_RISK_SCORE_RANGES_EDIT_ACTION,
    )
  ) {
    content = (
      <SettingForm
        control={control}
        errors={errors}
        isEditable={isEditable}
        settingId={settingId}
        riskLevelRangeError={riskLevelRangeError}
        activePermissions={activePermissions}
        clearErrors={clearErrors}
      />
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <>
      <SidePanel
        type={SidePanelType.AML}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={createError}
        sidepanelExpandedKey="risk-levels"
        footer={
          <>
            {(isEditable || !settingId) && (
              <ButtonPrimary
                text={
                  requestInProgress || updateInProgress
                    ? 'Loading...'
                    : 'Save category'
                }
                onClick={onFormSubmit}
                bold
              />
            )}
            <ButtonPseudo
              text="Cancel"
              className="button-white"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default SettingFormHOC;
